import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";

const NAV_LINKS = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Contact", href: "#contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isHome = location.pathname === "/";

    const handleAnchor = (e, href) => {
        if (!isHome) return;
        e.preventDefault();
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-brand-line"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-28 md:h-32 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 group">
                    <img
                        src={LOGO_URL}
                        alt="SheFleet Transportation"
                        width="108"
                        height="108"
                        fetchPriority="high"
                        decoding="async"
                        className="h-20 w-20 md:h-[108px] md:w-[108px] object-contain"
                    />
                    <div className="hidden sm:block leading-tight">
                        <div className="font-serif text-3xl text-brand-black tracking-tight">
                            SheFleet
                        </div>
                        <div className="text-xs tracking-[0.3em] uppercase text-brand-gold-deep">
                            Transportation
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={isHome ? l.href : `/${l.href}`}
                            onClick={(e) => handleAnchor(e, l.href)}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className="text-sm tracking-wide text-brand-black/80 hover:text-brand-gold-deep transition-colors relative group"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-px bg-brand-gold group-hover:w-full transition-all duration-300" />
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href="#booking"
                        onClick={(e) => handleAnchor(e, "#booking")}
                        data-testid="nav-cta-schedule"
                        className="px-6 py-3 bg-brand-black text-white text-xs tracking-[0.2em] uppercase hover:bg-brand-gold-deep transition-colors"
                    >
                        Schedule Pickup
                    </a>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden p-2 text-brand-black"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-brand-line bg-white">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={isHome ? l.href : `/${l.href}`}
                                onClick={(e) => handleAnchor(e, l.href)}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-base text-brand-black"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#booking"
                            onClick={(e) => handleAnchor(e, "#booking")}
                            data-testid="nav-mobile-cta-schedule"
                            className="mt-2 px-6 py-3 bg-brand-black text-white text-xs tracking-[0.2em] uppercase text-center"
                        >
                            Schedule Pickup
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
